import React, { FunctionComponent, memo, useCallback } from 'react';
import clsx from 'clsx';
import Image from 'material-ui-image';
import { DialogActions, Typography, Button, Grid } from '@material-ui/core';
import featureAccessoryDetailsModalStyles from './featureAccessoryDetailsModalStyles';
import { useCheckSelectedFeatures } from '../../../../../../hooks/features/useCheckSelectedFeatures';
import IFeature from '../../../../../../model/IFeature';
import OptionUnavailable from '../../../../../../../common/assets/images/optionUnavailable.png';
import ConfigurablePrice from '../../../../../presentational/prices/configurablePrice/ConfigurablePrice';
import useIsMobile from '../../../../../../../common/react/useIsMobile';
import ModalWithHeader from '../../../../../../../common/components/presentation/ModalWithHeader';
import Translation from '../../../../../../../gqlCommon/components/translation/Translation';

export interface IAccessoryDetailsModalProps {
  feature: IFeature;
  isSelected: boolean;
  isOpen: boolean;
  onClose: () => void;
}

const FeatureAccessoryDetailsModal: FunctionComponent<IAccessoryDetailsModalProps> = memo(
  ({ feature, isOpen, isSelected, onClose }) => {
    const styles = featureAccessoryDetailsModalStyles();
    const { name, prices, images = [], code } = feature || {};
    const extraDetails = feature?.attributes?.extradetails;
    const isMobile = useIsMobile();
    const { handleCheckboxChange } = useCheckSelectedFeatures();

    const onClickHandler = useCallback(() => {
      handleCheckboxChange(code);
      onClose();
    }, [handleCheckboxChange, onClose, code]);

    return (
      <ModalWithHeader
        open={isOpen}
        toggleModal={onClose}
        data-test={'customise:accessory-details'}
        modalProps={{ PaperProps: { className: styles.modalPaper } }}
        title={name}
        className={styles.modalRoot}
      >
        <Grid container={true}>
          <Grid item={true} xs={isMobile ? 12 : 5} className={styles.imageContainer}>
            <Image
              key={images[0]?.url}
              src={images[0]?.url || 'error'}
              alt={name}
              animationDuration={0}
              color="transparent"
              aspectRatio={1.75}
              data-test={'customise:accessory-details:image'}
              errorIcon={<img src={OptionUnavailable} alt={name} data-test={'customise:accessory:image:unavailable'} />}
            />
          </Grid>
          <Grid item={true} xs={isMobile ? 12 : 7} className={styles.modalContentContainer}>
            <Typography component="p" className={clsx([styles.title, styles.header])}>
              {name}
            </Typography>
            <ConfigurablePrice
              prices={prices}
              display={{ showOrLabel: false }}
              classes={{ single: { priceText: styles.header } }}
            />
            <Typography component="p" className={styles.description}>
              {extraDetails}
            </Typography>
          </Grid>
        </Grid>
        <DialogActions className={styles.modalActionsContainer}>
          <Button
            variant={isSelected ? 'outlined' : 'contained'}
            color={isSelected ? 'secondary' : 'primary'}
            onClick={onClickHandler}
            data-test="customise:accessory-details:cta"
            size="small"
            className={styles.button}
          >
            <Translation
              id={
                isSelected
                  ? 'trans__configurator__customise_page_feature_extra_details__modal_remove_cta'
                  : 'trans__configurator__customise_page_feature_extra_details__modal_select_cta'
              }
            />
          </Button>
        </DialogActions>
      </ModalWithHeader>
    );
  },
);

export default FeatureAccessoryDetailsModal;
